import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import useApiEp from "../../hooks/useApiEp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  getErrorResponse,
  getHeader,
  getToken,
  roundToMaxDecimals,
  toastError,
} from "../../utils/util";
import { UserContext } from "../../context/UserProvider";
import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ImageCropperModal from "../../widgets/ImageCropperModal";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { FaWallet, FaCoins } from "react-icons/fa";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { SiGooglesheets } from "react-icons/si";
import { GlassButton } from "../../themes/UiElements";
import { Phone, Settings2Icon } from "lucide-react";
import { useAccount } from "../../context/AccountContext";
import { usePhoneNumber } from "../../context/PhoneNumberContext";

// STYLES
const styles = {
  details: {
    padding: "1rem",
    borderTop: "1px solid #e1e1e1",
    display: "flex",
    alignItems: "center",
  },
  value: {
    padding: "1rem 2rem",
    borderTop: "1px solid #e1e1e1",
    color: "#899499",
  },
};

export default function ProfileCard(props) {
  const [showUploadPic, setShowUploadPic] = useState(false);
  const [connectingSheet, setConnectingSheet] = useState(false);

  // Access the global phone dialog function
  const { openPhoneDialog } = usePhoneNumber();

  const { selectedAccount } = useAccount();
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-account-balance?cid=${selectedAccount}`
  );
  const navigate = useNavigate();
  const { user, reloadUser } = useContext(UserContext);

  // Fetch all spreadsheet links
  const { data: MainData } = useApiEp("/user/get-spreadsheet-link");
  const { data: VoiceData } = useApiEp("/user/get-voice-spreadsheet-link");
  const { data: RcsData } = useApiEp("/user/get-rcs-spreadsheet-link");

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/spreadsheets",
    onSuccess: async (codeResponse) => {
      setConnectingSheet(true);
      try {
        const response = await axios.post(
          `${API_ENDPOINT}/api/google-auth`,
          {
            code: codeResponse.code,
          },
          getHeader()
        );
        toast.success(
          response.data.message || "Google Sheets connected successfully!"
        );
        document.location.reload();
        console.log("Server response:", response.data);
      } catch (err) {
        toastError(err);
      } finally {
        setConnectingSheet(false);
      }
    },
    onError: (errorResponse) => {
      console.log(errorResponse);
      toast.error("Failed to connect Google Sheets");
      setConnectingSheet(false);
    },
  });

  if (isLoading) {
    return <LinearProgress />;
  }
  if (isError) {
    return <Alert severity="warning">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Card variant="outlined">
      <ImageCropperModal
        open={showUploadPic}
        onClose={() => setShowUploadPic(false)}
        aspectRatio={1 / 1}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}/upload/upload-profile-image`}
        headers={{ Authorization: `Bearer ${getToken()}` }}
        onSuccess={(response) => {
          toast.success(response.message);
          reloadUser();
        }}
        onError={(error) => {
          toastError(error);
        }}
      />

      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item sx={{ p: "1.5rem 0rem", textAlign: "center" }}>
          {/* PROFILE PHOTO */}
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <IconButton
                onClick={() => {
                  setShowUploadPic(true);
                }}
                sx={{
                  border: "4px solid white",
                  backgroundColor: "#ff558f",
                  width: 32,
                  height: 32,
                  p: 0.5,
                  "&:hover": {
                    backgroundColor: "#ff3370",
                  },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 18, color: "white" }} />
              </IconButton>
            }
          >
            <Avatar
              sx={{ width: 100, height: 100, mb: 1.5 }}
              src={user?.profileImage}
            ></Avatar>
          </Badge>
          <Typography variant="h6">{user?.agentName}</Typography>
          <Typography color="text.secondary">{user?.userType}</Typography>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <Typography style={styles.details}>
              <FaWallet style={{ marginRight: "8px", color: "#899499" }} />
              Wallet
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "end" }}>
            <Link to={`/home/transaction-history`}>
              <Typography style={styles.value}>
                {data?.wallet ? (
                  <Chip
                    label={`${roundToMaxDecimals(data.wallet, 2)}`}
                    variant="outlined"
                    size="small"
                    color="info"
                  />
                ) : (
                  "NA"
                )}
              </Typography>
            </Link>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={6}>
            <Typography style={styles.details}>
              <FaCoins style={{ marginRight: "8px", color: "#899499" }} />
              AI Credits
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "end" }}>
            <Typography style={styles.value}>
              <Chip
                label={roundToMaxDecimals(data?.aiWallet ?? 0)}
                variant="outlined"
                size="small"
                color="info"
              />
            </Typography>
          </Grid>
        </Grid>

        {/* GOOGLE SHEETS INTEGRATION */}
        <Grid item xs={12} sx={{ p: 2, width: "100%" }}>
          {user?.googleSheetVerified ? (
            <Alert
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              severity="success"
              sx={{
                borderRadius: 2,
                alignItems: "center",
              }}
            >
              <AlertTitle>Google Sheets Connected</AlertTitle>
              Your chats are successfully linked and syncing with Google Sheets.
              <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {MainData?.link && (
                  <Button
                    fullWidth
                    component="a"
                    href={MainData.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<SiGooglesheets />}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    WABA Chats
                  </Button>
                )}

                {VoiceData?.link && (
                  <Button
                    fullWidth
                    component="a"
                    href={VoiceData.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<SiGooglesheets />}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    Voice Chats
                  </Button>
                )}

                {RcsData?.link && (
                  <Button
                    fullWidth
                    component="a"
                    href={RcsData.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<SiGooglesheets />}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    RCS Responses
                  </Button>
                )}

                {/* Fallback button if Google Sheet is verified but no links exist yet */}
                {!MainData?.link && !VoiceData?.link && !RcsData?.link && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<SiGooglesheets />}
                    onClick={googleLogin}
                    disabled={connectingSheet}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    {connectingSheet
                      ? "Connecting..."
                      : "Grant Spreadsheet Permission"}
                  </Button>
                )}
              </Box>
              <Box mt={2}>
                <GlassButton
                  fullWidth
                  onClick={() => {
                    navigate(`/home/sheet-schemas`);
                  }}
                  startIcon={<Settings2Icon />}
                >
                  Schema Settings
                </GlassButton>
              </Box>
            </Alert>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<SiGooglesheets />}
              onClick={googleLogin}
              disabled={connectingSheet}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 1,
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "rgba(52, 168, 83, 0.08)",
                },
              }}
            >
              {connectingSheet
                ? "Connecting..."
                : "Integrate Chats to Google Sheets"}
            </Button>
          )}
        </Grid>
        <Grid item xs={12} sx={{ width: "100%", px: 2, pb: 2 }}>
          <GlassButton
            fullWidth
            sx={{ mb: 1 }}
            startIcon={<Phone />}
            onClick={openPhoneDialog}
          >
            Add Phone numbers
          </GlassButton>
        </Grid>
      </Grid>
    </Card>
  );
}
